import React, { useMemo, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Chip,
  Container,
  CssBaseline,
  Grid,
  Link,
  MenuItem,
  TextField,
  ThemeProvider,
  Typography
} from '@mui/material';
import { StylesProvider } from '@mui/styles';
import {
  OpenInNew as OpenInNewIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import { getSiteTheme } from '../theme/siteTheme';
import { useDocumentThemeSync } from '../theme/useDocumentThemeSync';

const navigateToExternal = (url) => {
  if (typeof window !== 'undefined' && url) {
    window.location.assign(url);
  }
};

const WorkCatalog = ({ limit, onExternalNavigate = navigateToExternal }) => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const isDarkMode = useDocumentThemeSync();

  const workData = typeof window !== 'undefined' ? window.workData || {} : {};
  const workItems = Array.isArray(workData.work) ? workData.work : [];

  const visibleWorkItems = useMemo(() => {
    if (!Number.isFinite(limit)) {
      return workItems;
    }
    return workItems.slice(0, Math.max(0, limit));
  }, [workItems, limit]);

  const categories = useMemo(() => {
    const uniqueCategories = [...new Set(
      visibleWorkItems.flatMap((item) => (
        Array.isArray(item.categories) ? item.categories : []
      ))
    )];
    return ['all', ...uniqueCategories].filter(Boolean);
  }, [visibleWorkItems]);

  const filteredWork = useMemo(() => {
    const q = searchQuery.toLowerCase();

    return visibleWorkItems.filter((item) => {
      const title = (item.title || '').toLowerCase();
      const description = (item.description || '').toLowerCase();
      const matchesSearch = !q || title.includes(q) || description.includes(q);
      const itemCategories = Array.isArray(item.categories) ? item.categories : [];
      const matchesCategory = selectedCategory === 'all' || itemCategories.includes(selectedCategory);
      return matchesSearch && matchesCategory;
    });
  }, [visibleWorkItems, searchQuery, selectedCategory]);

  const theme = useMemo(() => getSiteTheme(isDarkMode), [isDarkMode]);

  return (
    <StylesProvider injectFirst>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Container
          maxWidth="xl"
          disableGutters
          sx={{
            py: { xs: 2, md: 3 },
            px: { xs: 0, sm: 2, md: 3 },
            mx: 'auto',
            transition: 'background-color 0.3s ease',
            width: '100%',
            '@media (min-width: 1600px)': {
              maxWidth: '1480px',
            },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              gap: { xs: 2, md: 2.5 },
              alignItems: { md: 'center' },
              mb: { xs: 3, md: 4 },
            }}
          >
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Search work..."
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              InputProps={{
                startAdornment: <SearchIcon color="action" sx={{ mr: 1 }} />,
                'aria-label': 'Search work items',
              }}
            />
            <TextField
              select
              value={selectedCategory}
              onChange={(event) => setSelectedCategory(event.target.value)}
              variant="outlined"
              sx={{ minWidth: 220 }}
              inputProps={{ 'aria-label': 'Filter work by category' }}
            >
              {categories.map((category) => (
                <MenuItem key={category} value={category}>
                  {category === 'all' ? 'All Categories' : category}
                </MenuItem>
              ))}
            </TextField>
          </Box>

          <Grid
            container
            columnSpacing={{ xs: 0, sm: 2, lg: 3 }}
            rowSpacing={{ xs: 3, md: 4 }}
          >
            {filteredWork.map((item) => {
              const href = item.url || item.external_link || '#';
              const externalHref = item.external_link || '';
              const showExternalLink = Boolean(item.url && externalHref);
              const isPrimaryExternal = !item.url && Boolean(externalHref);
              const itemCategories = Array.isArray(item.categories) ? item.categories : [];
              const stopCardNavigation = (event) => {
                event.stopPropagation();
              };
              const handleCardClick = (event) => {
                if (!externalHref || event.defaultPrevented) {
                  return;
                }

                const target = event.target;
                if (target instanceof Element && target.closest('a, button, input, select, textarea')) {
                  return;
                }

                onExternalNavigate(externalHref);
              };
              const handleCardKeyDown = (event) => {
                if (!externalHref) {
                  return;
                }

                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  onExternalNavigate(externalHref);
                }
              };

              return (
                <Grid item xs={12} sm={6} md={4} key={item.slug || item.title}>
                  <Card
                    onClick={handleCardClick}
                    onKeyDown={handleCardKeyDown}
                    role={externalHref ? 'link' : undefined}
                    tabIndex={externalHref ? 0 : undefined}
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      borderRadius: 2,
                      boxShadow: isDarkMode
                        ? '0 8px 20px rgba(0, 0, 0, 0.35)'
                        : '0 8px 24px rgba(47, 46, 42, 0.12)',
                      borderColor: isDarkMode
                        ? 'rgba(255,255,255,0.08)'
                        : 'rgba(47,46,42,0.08)',
                      borderWidth: 1,
                      borderStyle: 'solid',
                      transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: isDarkMode
                          ? '0 12px 28px rgba(0, 0, 0, 0.45)'
                          : '0 12px 32px rgba(47, 46, 42, 0.16)',
                      },
                      backgroundImage: 'none',
                    }}
                  >
                    <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 2 }}>
                        {item.icon && (
                          <Box
                            component="img"
                            src={item.icon}
                            alt=""
                            sx={{ width: 42, height: 42, borderRadius: 1, objectFit: 'cover' }}
                          />
                        )}
                        <Box
                          sx={{
                            flexGrow: 1,
                            minWidth: 0,
                            display: 'flex',
                            alignItems: 'flex-start',
                            justifyContent: 'space-between',
                            gap: 1,
                          }}
                        >
                          <Link
                            href={href}
                            underline="hover"
                            color="inherit"
                            target={isPrimaryExternal ? '_blank' : undefined}
                            rel={isPrimaryExternal ? 'noopener noreferrer' : undefined}
                            onClick={showExternalLink ? stopCardNavigation : undefined}
                            sx={{
                              display: 'inline-flex',
                              alignItems: 'flex-start',
                              minWidth: 0,
                              flex: '1 1 auto',
                              '&:hover, &:focus-visible': {
                                '& .work-title': {
                                  fontWeight: 700,
                                },
                              },
                            }}
                          >
                            <Typography
                              variant="h6"
                              component="h2"
                              className="work-title"
                              sx={{
                                transition: 'font-weight 0.2s',
                                lineHeight: 1.35,
                                overflowWrap: 'anywhere',
                              }}
                            >
                              {item.title}
                            </Typography>
                          </Link>
                          {showExternalLink && (
                            <Link
                              href={externalHref}
                              target="_blank"
                              rel="noopener noreferrer"
                              aria-label={`Also published on LinkedIn: ${item.title}`}
                              color="text.secondary"
                              onClick={stopCardNavigation}
                              sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flex: '0 0 auto',
                                alignSelf: 'flex-start',
                                minWidth: 24,
                                minHeight: 24,
                                mt: 0.125,
                              }}
                            >
                              <OpenInNewIcon fontSize="inherit" />
                            </Link>
                          )}
                          {isPrimaryExternal && <OpenInNewIcon fontSize="inherit" />}
                        </Box>
                      </Box>

                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, flexGrow: 1 }}>
                        {item.description}
                      </Typography>

                      {itemCategories.length > 0 && (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 'auto' }}>
                          {itemCategories.map((category, index) => (
                            <Chip
                              key={`${category}-${index}`}
                              label={category}
                              size="small"
                              variant="outlined"
                              onClick={() => setSelectedCategory(category)}
                              sx={{ cursor: 'pointer' }}
                            />
                          ))}
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>

          {filteredWork.length === 0 && (
            <Box sx={{ py: 6, textAlign: 'center' }}>
              <Typography variant="body1" color="text.secondary">
                No work items match your search and filter settings.
              </Typography>
            </Box>
          )}
        </Container>
      </ThemeProvider>
    </StylesProvider>
  );
};

export default WorkCatalog;
